import { BarChart as MuiBarChart } from '@mui/x-charts/BarChart';
import MuiBox from '@mui/material/Box';
import { useChartColors } from '../theme/chartPalette';

/**
 * The application's only chart.
 *
 * Wrapping @mui/x-charts here rather than importing it in pages keeps the same
 * rule the rest of this library exists for: presentation decisions live in one
 * place. It also means the chart conventions below are applied to every chart by
 * construction instead of by each caller remembering them.
 *
 * Only the open-source (MIT) @mui/x-charts package is used — nothing from the
 * commercial Pro or Premium tiers.
 *
 * The conventions, and why:
 *
 *   - A legend whenever there is more than one series, and none for a single
 *     series (the title already names it). Identity must never rest on colour
 *     alone.
 *   - A horizontal hairline grid only. Vertical rules add noise without helping
 *     anyone read a value off a bar.
 *   - No label printed on every bar. The axis and the tooltip carry values, and
 *     the table view beside the chart carries all of them exactly.
 *   - Rounded bar ends, thin marks, generous margins.
 *   - The height covers the axis band as well as the plot, so a card never grows
 *     its own little scrollbar to reach the labels.
 */
/** The thickest a bar is allowed to be. Beyond this it reads as a slab. */
const MAX_BAR_THICKNESS = 24;

/**
 * The share of each band left as air, chosen so bars stay within the thickness
 * cap.
 *
 * Material UI has no maximum-bar-width option — a bar fills whatever the band
 * leaves it — so with few categories in a tall chart the bars come out as heavy
 * blocks. Deriving the gap from the available height keeps a four-row chart as
 * thin as a twelve-row one.
 *
 * The 56px allows for the value axis band and the top and bottom margins.
 */
function horizontalGapRatio(height, categoryCount) {
  if (!categoryCount) return 0.4;

  const band = Math.max(1, (height - 56) / categoryCount);
  // Clamped: never so tight that bars vanish, never so loose that a dense chart
  // turns into hairlines.
  return Math.min(0.85, Math.max(0.25, 1 - MAX_BAR_THICKNESS / band));
}

export function Chart({
  categories,
  categoryColors,
  series,
  horizontal = false,
  height = 320,
  valueFormatter,
  axisFormatter,
  onSelect,
  loading = false,
  ariaLabel
}) {
  const colors = useChartColors();

  const bandAxis = {
    scaleType: 'band',
    data: categories,
    // Space between bars comes from the band, not from a stroke around each
    // one — a border drawn to separate marks reads as chrome.
    categoryGapRatio: horizontal ? horizontalGapRatio(height, categories.length) : 0.3,
    barGapRatio: 0.15,
    /**
     * A gutter wide enough for the category names.
     *
     * Only in the horizontal layout, where the band axis carries counterparty
     * names rather than short month labels. Without it Material UI keeps the
     * default narrow gutter and truncates them to "Oce…", "Coas…" — which
     * leaves the reader unable to tell which supplier a bar belongs to, the one
     * thing this level of the chart exists to say.
     */
    ...(horizontal ? { width: 150 } : null),
    /**
     * A colour per category, for the rare chart whose bars mean different
     * *kinds* of thing rather than one measure across a dimension — money in,
     * money out, and the result.
     *
     * Not to be used to shade bars by size. Colouring a bar darker because it
     * is bigger re-encodes the length that is already on screen and spends the
     * only free channel saying nothing new.
     */
    ...(categoryColors
      ? { colorMap: { type: 'ordinal', values: categories, colors: categoryColors } }
      : null)
  };

  // Ticks get the short form and tooltips the full one. A column of complete
  // rupee figures down the axis costs a third of the plot's width to say
  // something the tooltip says exactly.
  const tick = axisFormatter ?? valueFormatter;
  const valueAxis = {
    valueFormatter: (value) => tick?.(value) ?? value,
    width: horizontal ? 64 : 72,
    // A handful of gridlines, not one per 20,000 rupees. The default tick
    // density on a wide horizontal axis produces a wall of labels that is
    // slower to read than no labels at all — the tooltip and the table view
    // carry exact values.
    tickNumber: 6
  };

  const resolved = series.map((entry, index) => ({
    ...entry,
    // Colour comes from the fixed slot order, never from the series' rank in a
    // sorted list.
    color: entry.color ?? colors[index % colors.length],
    valueFormatter
  }));

  return (
    <MuiBox
      role="img"
      aria-label={ariaLabel}
      sx={{
        width: '100%',
        // Held at reduced opacity while refetching rather than replaced by a
        // skeleton, so the numbers do not flash away and the layout never jumps.
        opacity: loading ? 0.5 : 1,
        transition: 'opacity 150ms ease'
      }}
    >
      <MuiBarChart
        height={height}
        layout={horizontal ? 'horizontal' : 'vertical'}
        series={resolved}
        xAxis={[horizontal ? valueAxis : bandAxis]}
        yAxis={[horizontal ? bandAxis : valueAxis]}
        grid={horizontal ? { vertical: true } : { horizontal: true }}
        hideLegend={series.length < 2}
        borderRadius={4}
        onItemClick={
          onSelect
            ? (event, item) => onSelect({ seriesId: item.seriesId, index: item.dataIndex })
            : undefined
        }
        margin={{ left: 8, right: 16, top: 8, bottom: 8 }}
        sx={onSelect ? { '& .MuiBarElement-root': { cursor: 'pointer' } } : undefined}
      />
    </MuiBox>
  );
}
